import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowUpRight, BookOpen, ShieldCheck } from "lucide-react";
import { ORDERED_VENDORS, LOGOS, vendorBySlug } from "@/lib/vendors";
import BgLayer from "@/components/ui-shared/BgLayer";

const SLIDESHOW_SLUGS = [
    "aws", "microsoft", "gcp", "sap", "redhat",
    "ibm", "oracle", "dell-emc", "github", "isc2",
];

const HeroSlideshow = () => {
    const slides = useMemo(
        () =>
            SLIDESHOW_SLUGS
                .map((slug) => {
                    const v = vendorBySlug(slug);
                    return v ? { slug, name: v.name, image: v.banner } : null;
                })
                .filter(Boolean),
        []
    );
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        if (paused || slides.length <= 1) return undefined;
        timerRef.current = setInterval(() => {
            setIndex((i) => (i + 1) % slides.length);
        }, 4000);
        return () => clearInterval(timerRef.current);
    }, [paused, slides.length]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            className="relative rounded-2xl overflow-hidden border border-[#C266FF]/20"
            data-testid="partners-hero-slideshow"
        >
            <div className="relative w-full h-[340px]">
                {slides.map((s, i) => (
                    <img
                        key={s.slug}
                        src={s.image}
                        alt={`${s.name} partner`}
                        loading="lazy"
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
                            i === index ? "opacity-100 z-[1]" : "opacity-0 z-0"
                        }`}
                        data-testid={`partners-hero-slide-${s.slug}`}
                    />
                ))}
                <div className="absolute inset-0 bg-gradient-to-t from-[#07020F] via-transparent to-transparent z-[2] pointer-events-none" />
            </div>

            {/* Pagination dots */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[3] flex items-center gap-2">
                {slides.map((s, i) => (
                    <button
                        key={s.slug}
                        type="button"
                        onClick={() => setIndex(i)}
                        aria-label={`Show ${s.name}`}
                        data-testid={`partners-hero-dot-${s.slug}`}
                        className={`h-1.5 rounded-full transition-all duration-500 ${
                            i === index ? "w-6 bg-white" : "w-1.5 bg-white/40 hover:bg-white/70"
                        }`}
                    />
                ))}
            </div>
        </motion.div>
    );
};

const initials = (name) =>
    name
        .replace(/\(.*?\)/g, "")
        .split(/\s|–|-/)
        .filter(Boolean)
        .map((w) => w[0])
        .join("")
        .slice(0, 3)
        .toUpperCase();

const VendorCard = ({ v, i }) => {
    const logo = LOGOS[v.slug];
    return (
        <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.42, delay: (i % 10) * 0.03 }}
            data-testid={`partners-page-tile-${i}`}
        >
            <Link
                to={`/partners/${v.slug}`}
                className="group relative block h-full rounded-2xl border border-white/8 bg-gradient-to-br from-white/[0.04] to-white/[0.01] p-6 overflow-hidden transition-all duration-500 hover:border-[#C266FF]/40 hover:-translate-y-1"
                style={{ "--vendor-hue": v.hue }}
            >
                {/* Ambient hue wash */}
                <div
                    className="pointer-events-none absolute -top-24 -right-16 w-56 h-56 rounded-full blur-3xl opacity-0 group-hover:opacity-40 transition-opacity duration-700"
                    style={{ background: `radial-gradient(circle, ${v.hue} 0%, transparent 70%)` }}
                />
                {/* Corner arrow */}
                <div className="absolute top-5 right-5 w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-white/70 group-hover:bg-[#A100FF] group-hover:border-[#A100FF] group-hover:text-white transition-all duration-300">
                    <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-500" />
                </div>

                {/* Logo mark */}
                <div className="flex items-center gap-4">
                    <div
                        className="w-14 h-14 rounded-xl bg-white flex items-center justify-center shrink-0 overflow-hidden shadow-[0_8px_20px_-8px_rgba(0,0,0,0.6)]"
                        style={{ borderTop: `2px solid ${v.hue}` }}
                    >
                        {logo ? (
                            <img
                                src={logo}
                                alt={`${v.name} logo`}
                                loading="lazy"
                                className="max-w-[70%] max-h-[70%] object-contain"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                        ) : (
                            <span
                                className="font-display font-bold text-[15px] tracking-tight"
                                style={{ color: v.hue }}
                            >
                                {initials(v.name)}
                            </span>
                        )}
                    </div>
                </div>

                {/* Vendor name */}
                <h3 className="mt-6 font-display text-white text-[17px] leading-snug font-semibold tracking-tight min-h-[46px]">
                    {v.name}
                </h3>

                {/* Divider with course count highlight */}
                <div className="mt-5 flex items-end justify-between gap-3 pt-4 border-t border-white/8">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#75668C] font-display">Certified programs</div>
                        <div className="mt-1 flex items-baseline gap-1.5">
                            <span
                                className="font-display font-medium text-4xl leading-none tracking-tight"
                                style={{
                                    background: `linear-gradient(135deg, ${v.hue} 0%, #E9CCFF 120%)`,
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                                data-testid={`partners-course-count-${v.slug}`}
                            >
                                {v.courses.length}
                            </span>
                            <span className="text-[13px] text-[#B0A3C4] font-display">courses</span>
                        </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-display text-[#B0A3C4] group-hover:text-[#C266FF] transition-colors">
                        Explore <ArrowUpRight className="w-3 h-3" />
                    </span>
                </div>
            </Link>
        </motion.div>
    );
};

const Partners = () => {
    const [query, setQuery] = useState("");

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return ORDERED_VENDORS;
        return ORDERED_VENDORS.filter((v) => v.name.toLowerCase().includes(q) || v.slug.includes(q));
    }, [query]);

    const totalCourses = useMemo(
        () => ORDERED_VENDORS.reduce((sum, v) => sum + v.courses.length, 0),
        []
    );

    return (
        <div data-testid="partners-page">
            {/* Hero */}
            <section className="relative pt-32 md:pt-40 pb-16 overflow-hidden">
                <BgLayer variant="mesh" intensity="strong" />
                <div className="sxe-container relative grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
                    <div>
                        <div className="overline"><span className="dot" /> Partner Offerings</div>
                        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter leading-[1.02] text-white mt-6">
                            <span className="text-gradient">25+ global partners</span>. One certified stack.
                        </h1>
                        <p className="text-[#B0A3C4] text-lg md:text-xl leading-relaxed mt-6 max-w-xl">
                            SpringXE&apos;s programs are officially aligned with the world&apos;s leading technology and standards bodies — {totalCourses}+ certified courses, one enterprise-grade learning experience.
                        </p>

                        {/* Stats row */}
                        <div className="mt-8 grid grid-cols-3 gap-3 max-w-xl">
                            {[
                                { k: `${ORDERED_VENDORS.length}`, l: "OEM partners" },
                                { k: `${totalCourses}+`, l: "certified courses" },
                                { k: "9", l: "learning tracks" },
                            ].map((s) => (
                                <div key={s.l} className="sxe-card p-4">
                                    <div className="font-display text-3xl md:text-4xl text-white tracking-tight">{s.k}</div>
                                    <div className="text-[11px] uppercase tracking-widest text-[#75668C] font-display mt-1">{s.l}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <HeroSlideshow />
                </div>
            </section>

            {/* Directory */}
            <section className="pb-28 relative">
                <BgLayer variant="dots" />
                <div className="sxe-container relative">
                    {/* Filter bar */}
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
                        <div className="max-w-xl">
                            <div className="overline"><span className="dot" /> Partner directory</div>
                            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tighter leading-[1.05] text-white mt-3">
                                Explore <span className="text-gradient">certified programs</span> by partner
                            </h2>
                        </div>
                        <div className="relative w-full md:w-80">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#75668C]" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search AWS, ISTQB, Siemens..."
                                data-testid="partners-search-input"
                                className="w-full pl-11 pr-4 py-3 rounded-full bg-white/[0.03] border border-white/10 text-white placeholder-[#75668C] font-display text-sm focus:outline-none focus:border-[#C266FF]/50 transition-colors"
                            />
                        </div>
                    </div>

                    {/* Trust strip */}
                    <div className="mb-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-[13px] text-[#B0A3C4] font-display">
                        <div className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#C266FF]" /> Official OEM curriculum</div>
                        <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#C266FF]" /> Certified assessments</div>
                        <div className="flex items-center gap-2"><ArrowUpRight className="w-4 h-4 text-[#C266FF]" /> Cohort-ready delivery</div>
                    </div>

                    {/* Grid */}
                    {filtered.length === 0 ? (
                        <div className="sxe-card p-12 text-center">
                            <p className="text-[#B0A3C4] font-display">No partners match &ldquo;{query}&rdquo;.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                            {filtered.map((v, i) => (
                                <VendorCard key={v.slug} v={v} i={i} />
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Partners;
